import { apiErrorMessage } from '@lib/api/apiError';
import { useProfileQuery } from '@lib/api/profile';
import { logError } from '@lib/logError';
import braintree, { type BraintreeHostedFields } from 'braintree-web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AddCardDialog } from './add-card-dialog';
import { hostedFieldStyles } from './hostedFields';
import {
  useAddPaymentMethodMutation,
  useClientTokenQuery,
  useMarkCardDefaultMutation,
  usePaymentMethodsQuery,
  useRemoveCardMutation,
} from './paymentApi';
import { type PaymentProfile } from './paymentTypes';
import { SavedCards } from './saved-cards';


const Braintree = (): JSX.Element => {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const { data: profileResult = {} } = useProfileQuery();
  const userDetails = profileResult as PaymentProfile;

  const { data: savedCards = [], isPending: cardsLoading } =
    usePaymentMethodsQuery();
  const { data: clientToken = null } = useClientTokenQuery();
  const addPaymentMethod = useAddPaymentMethodMutation();
  const markCardDefault = useMarkCardDefaultMutation();
  const removeCard = useRemoveCardMutation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const hostedFieldsRef = useRef<BraintreeHostedFields | null>(null);
  const selectedCardToken =
    savedCards.find((card) => card.default)?.token ??
    savedCards[0]?.token ??
    null;
  const selectingToken = markCardDefault.isPending
    ? markCardDefault.variables ?? null
    : null;
  const deletingToken = removeCard.isPending
    ? removeCard.variables ?? null
    : null;

  useEffect(() => {
    if (localStorage.getItem('userRole') === 'buyer') {
      if (userDetails.data?.user?.phoneNumber) {
        const { firstName, email } = userDetails.data.user;
        if (!firstName || !email) {
          navigator('/personal-info', {
            state: { massage: t('profile.completeProfile') },
          });
        }
      }
    }
  }, [navigator, t, userDetails]);

  useEffect(() => {
    if (!showAddModal || !clientToken) return;

    let hostedFields: BraintreeHostedFields | undefined;
    setIsReady(false);

    const timer = setTimeout(() => {
      braintree.client
        .create({ authorization: clientToken })
        .then((clientInstance) =>
          braintree.hostedFields.create({
            client: clientInstance,
            styles: hostedFieldStyles(),
            fields: {
              number: {
                container: '#bt-card-number',
                placeholder: t('payments.cardNumberPlaceholder'),
              },
              expirationDate: {
                container: '#bt-expiry',
                placeholder: t('payments.expiryPlaceholder'),
              },
              cvv: {
                container: '#bt-cvv',
                placeholder: t('payments.cvvPlaceholder'),
              },
              cardholderName: {
                container: '#bt-name',
                placeholder: t('payments.namePlaceholder'),
              },
            },
          })
        )
        .then((instance) => {
          hostedFields = instance;
          hostedFieldsRef.current = instance;
          instance.on('validityChange', () => {
            const allValid = Object.values(instance.getState().fields).every(
              (field) => field.isValid
            );
            setIsReady(allValid);
          });
        })
        .catch((error: unknown) => logError('payment fields', error));
    }, 100);

    return () => {
      clearTimeout(timer);
      void hostedFields?.teardown();
      hostedFieldsRef.current = null;
    };
  }, [clientToken, showAddModal, t]);

  const handleCloseModal = (): void => {
    if (addPaymentMethod.isPending) return;
    setShowAddModal(false);
    setIsReady(false);
  };

  const handleSave = async (): Promise<void> => {
    if (!hostedFieldsRef.current) return;
    try {
      const { nonce } = await hostedFieldsRef.current.tokenize();
      await addPaymentMethod.mutateAsync(nonce);
      setShowAddModal(false);
      setIsReady(false);
    } catch {
      // Tokenizing or saving failed. The dialog stays open so the user can retry.
    }
  };

  const handleSelect = (token: string): void => {
    if (
      token === selectedCardToken ||
      markCardDefault.isPending ||
      removeCard.isPending
    ) {
      return;
    }
    markCardDefault.mutate(token, {
      onSuccess: () => {
        toast.success(String(t('payments.defaultUpdated')));
      },
      onError: (error: unknown) => {
        toast.error(
          apiErrorMessage(error) ?? String(t('payments.defaultUpdateFailed'))
        );
      },
    });
  };

  const handleDelete = (token: string): void => {
    if (markCardDefault.isPending || removeCard.isPending) {
      return;
    }
    removeCard.mutate(token, {
      onSuccess: () => {
        toast.success(String(t('payments.cardRemoved')));
      },
      onError: (error: unknown) => {
        toast.error(
          apiErrorMessage(error) ?? String(t('payments.cardRemoveFailed'))
        );
      },
    });
  };

  return (
    <>
      <SavedCards
        cards={savedCards}
        loading={cardsLoading}
        selectedToken={selectedCardToken}
        selectingToken={selectingToken}
        deletingToken={deletingToken}
        busy={markCardDefault.isPending || removeCard.isPending}
        canAdd={Boolean(clientToken)}
        onAdd={() => setShowAddModal(true)}
        onSelect={handleSelect}
        onDelete={handleDelete}
      />
      <AddCardDialog
        open={showAddModal}
        clientToken={clientToken}
        ready={isReady}
        saving={addPaymentMethod.isPending}
        onClose={handleCloseModal}
        onSave={() => {
          void handleSave();
        }}
      />
    </>
  );
};

export default Braintree;
